import { forwardRef, useEffect, useState, type ReactNode } from 'react';
import {
  Text,
  Group,
  Tooltip,
  Stack,
  Loader,
  Center,
  Progress,
  ActionIcon,
  Menu,
  Box,
  Card,
} from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import { ViewPanel } from '@ayocore/platform/client/components/EntityViewLayout';
import type { DealEvent } from '../../../../../schemas/deal-event.schema';
import type { DealStatus } from '../../../../../schemas/deal-status.schema';
import { useDealStatusRepository } from '../../../../rpc';
import logger from '@ayocore/platform/common/helpers/logger';

/**
 * Колонка funnel-а: интерактивный wrapper с hover-фоном для кликабельных статусов.
 * Расширяет hit-зону (padding) и явно сигнализирует кликабельность.
 *
 * forwardRef нужен для Mantine Tooltip — без него tooltip не получает hover-сигналы.
 */
const StatusCell = forwardRef<
  HTMLDivElement,
  { isClickable: boolean; onClick?: () => void; children: ReactNode }
>(({ isClickable, onClick, children }, ref) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      ref={ref}
      onClick={isClickable ? onClick : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: isClickable ? 'pointer' : 'default',
        padding: '4px 6px',
        borderRadius: 6,
        background: hovered && isClickable ? 'var(--mantine-color-gray-1)' : 'transparent',
        transition: 'background 0.1s ease',
        // Растягиваем ячейку на всю высоту, чтобы внутренний Stack мог
        // прижать Progress к низу через marginTop: auto.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </Box>
  );
});
StatusCell.displayName = 'StatusCell';

interface DealStatusHistoryProps {
  statusHistory: DealEvent[];
  currentStatusId: number | null;
  dealTypeId: number | null;
  onStatusClick?: (status: DealStatus) => void;
}

/**
 * Компонент для отображения всех возможных статусов сделки.
 */
export function DealStatusHistory({
  statusHistory,
  currentStatusId,
  dealTypeId,
  onStatusClick,
}: DealStatusHistoryProps) {
  const [allStatuses, setAllStatuses] = useState<DealStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const dealStatusRepository = useDealStatusRepository();

  // Загружаем все статусы для типа сделки
  useEffect(() => {
    if (!dealTypeId) {
      setAllStatuses([]);
      setLoading(false);
      return;
    }

    const loadStatuses = async () => {
      try {
        setLoading(true);
        const statuses = await dealStatusRepository
          .filterByType(dealTypeId)
          .filterActive()
          .orderBy('sort_order', 'asc')
          .limit(1000);
        setAllStatuses(statuses);
      } catch (error) {
        logger.error('Failed to load deal statuses:', error);
        setAllStatuses([]);
      } finally {
        setLoading(false);
      }
    };

    loadStatuses();
  }, [dealTypeId]);

  // Извлекаем ID статусов, которые уже были в истории
  const passedStatusIds = new Set<number>();
  if (statusHistory && statusHistory.length > 0) {
    statusHistory.forEach(event => {
      if (event.to_status?.id) {
        passedStatusIds.add(event.to_status.id);
      }
    });
  }

  // Текущий статус тоже считается пройденным
  if (currentStatusId) {
    passedStatusIds.add(currentStatusId);
  }

  const getEventInfo = (statusId: number) => {
    const event = statusHistory?.find(e => e.to_status?.id === statusId);
    return event
      ? {
          date: event.created_at,
          createdBy: event.created_by,
          source: event.source,
        }
      : null;
  };

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatSource = (source: DealEvent['source']) => {
    switch (source) {
      case 'user':
        return 'Пользователь';
      case 'agent':
        return 'Агент';
      case 'cron':
        return 'Система';
      default:
        return source;
    }
  };

  const sortedStatuses = [...allStatuses].sort((a, b) => {
    const aOrder = a.sort_order ?? 0;
    const bOrder = b.sort_order ?? 0;
    return aOrder - bOrder;
  });

  // Позитивный флоу — основной ряд статусов
  const positiveStatuses = sortedStatuses.filter(s => !s.is_negative);
  // Негативные статусы — показываем в [...] меню, кроме текущего (если сбылся)
  const negativeStatuses = sortedStatuses.filter(s => s.is_negative);
  const currentIsNegative = negativeStatuses.some(s => s.id === currentStatusId);

  // Статусы для отображения в ряду: позитивные + текущий негативный (если сбылся)
  const visibleStatuses = currentIsNegative
    ? [
        ...positiveStatuses.filter(s => !s.is_final),
        ...negativeStatuses.filter(s => s.id === currentStatusId),
      ]
    : positiveStatuses;

  // Негативные для меню [...]: все негативные кроме текущего (он уже в ряду)
  const menuNegativeStatuses = negativeStatuses.filter(s => s.id !== currentStatusId);

  const currentIndex = currentStatusId
    ? visibleStatuses.findIndex(status => status.id === currentStatusId)
    : -1;

  const getDaysInStatus = (statusId: number): number | null => {
    const statusChangeEvents = statusHistory.filter(e => e.event_type === 'status_change');
    const enterEvent = statusChangeEvents.find(e => e.to_status?.id === statusId);
    if (!enterEvent) return null;

    const enterDate = new Date(enterEvent.created_at);
    const exitEvent = statusChangeEvents.find(
      e => e.from_status?.id === statusId && new Date(e.created_at) > enterDate
    );

    const exitDate = exitEvent ? new Date(exitEvent.created_at) : new Date();
    const diffMs = exitDate.getTime() - enterDate.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  const formatDaysInStatus = (days: number): string => {
    return `${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'}`;
  };

  return (
    <Group gap={4} wrap='nowrap' align='stretch'>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ViewPanel>
          {loading ? (
            <Center>
              <Loader size='xs' />
            </Center>
          ) : sortedStatuses.length === 0 ? (
            <Text size='xs' c='dimmed'>
              {dealTypeId ? 'Нет доступных статусов' : 'Тип сделки не указан'}
            </Text>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${visibleStatuses.length}, 1fr)`,
                alignItems: 'stretch',
                gap: 0,
              }}
            >
              {visibleStatuses.map((status, idx) => {
                const isPassed = passedStatusIds.has(status.id);
                const isCurrent = status.id === currentStatusId;
                const isClickable = onStatusClick != null && !isCurrent;
                const isNegative = status.is_negative;
                const isAfterCurrent = currentIndex !== -1 && idx > currentIndex;
                // Зелёным — только реально пройденные статусы (есть в истории
                // событий) и текущий. Статусы, через которые сделка не проходила
                // (например «перепрыгнутый» Оплачен), остаются серыми.
                const isActivelyPassed = isPassed && !isAfterCurrent;
                const daysInStatus = getDaysInStatus(status.id);

                let progressColor: string;
                let progressValue: number;
                if (isNegative && isCurrent) {
                  progressColor = 'red';
                  progressValue = 100;
                } else if (isActivelyPassed) {
                  progressColor = 'green';
                  progressValue = 100;
                } else if (isAfterCurrent && isPassed) {
                  // Побывали в статусе, но потом откатились назад — тот же
                  // серый, что и у непосещённого статуса (gray.5, см. ветку
                  // ниже), но на пару тонов темнее (primaryShade: 5 в теме).
                  progressColor = 'gray.5';
                  progressValue = 100;
                } else {
                  progressColor = 'gray';
                  progressValue = 0;
                }

                const tooltipLabel = isPassed
                  ? (() => {
                      const eventInfo = getEventInfo(status.id);
                      const tooltipParts: string[] = [];
                      if (eventInfo) {
                        tooltipParts.push(`Дата: ${formatDate(eventInfo.date)}`);
                        if (eventInfo.createdBy?.name)
                          tooltipParts.push(`Пользователь: ${eventInfo.createdBy.name}`);
                        tooltipParts.push(`Источник: ${formatSource(eventInfo.source)}`);
                      }
                      if (daysInStatus !== null)
                        tooltipParts.push(`Дней в статусе: ${daysInStatus}`);
                      return tooltipParts.length > 0
                        ? tooltipParts.join('\n')
                        : 'Статус был пройден';
                    })()
                  : isClickable
                    ? 'Изменить статус'
                    : null;

                return (
                  <Tooltip
                    key={status.id}
                    label={tooltipLabel}
                    withArrow
                    disabled={tooltipLabel == null}
                  >
                    <StatusCell
                      isClickable={isClickable}
                      onClick={isClickable ? () => onStatusClick(status) : undefined}
                    >
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text
                          size='xs'
                          fw={isActivelyPassed ? 600 : 400}
                          c={
                            isNegative && isCurrent ? 'red' : isActivelyPassed ? 'dark' : 'dimmed'
                          }
                          ta='left'
                          style={{ fontSize: '0.7rem' }}
                        >
                          {status.name}
                        </Text>
                        <Text
                          size='xs'
                          c='dimmed'
                          ta='left'
                          style={{
                            fontSize: '0.65rem',
                            visibility: daysInStatus !== null ? 'visible' : 'hidden',
                          }}
                        >
                          {daysInStatus !== null ? formatDaysInStatus(daysInStatus) : ' '}
                        </Text>
                        <Progress
                          size='md'
                          color={progressColor}
                          value={progressValue}
                          transitionDuration={0}
                          style={{ marginTop: 'auto' }}
                        />
                      </Stack>
                    </StatusCell>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </ViewPanel>
      </div>

      {menuNegativeStatuses.length > 0 && (
        <Card
          padding='xs'
          shadow='none'
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Menu withArrow position='bottom-end' withinPortal>
            <Menu.Target>
              <ActionIcon variant='subtle' size='xs' color='red'>
                <IconDots size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {menuNegativeStatuses.map(status => (
                <Menu.Item
                  key={status.id}
                  color='red'
                  disabled={!onStatusClick}
                  onClick={() => onStatusClick?.(status)}
                >
                  {status.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Card>
      )}
    </Group>
  );
}
